/**
 * `useTeamRangeRoster` — shared TanStack Query hook for the stitched
 * team range-roster endpoints (issue #1280; backend contract #1279).
 *
 *   Lead:   `GET /api/teams/{team_id}/roster?from=&to=`
 *   Member: `GET /api/teams/{team_id}/roster/member?from=&to=`
 *
 * Mirrors `useCycleRoster` (issue #127) — same cache-key discipline,
 * `staleTime`, `retry: false`, and the `memberView` endpoint switch —
 * so the roster page (`<TeamRosterPage/>`, #1956) and any future
 * consumer (print, stats overlay) share one cache per (team, window,
 * account, role) rather than each firing their own fetch.
 *
 * `staleTime: 30_000` — same rationale as `useCycleRoster`: the roster
 * payload changes on every solve / publish, so a tight window keeps the
 * cache from going stale across solves the user did NOT trigger from
 * this surface, while tab swaps within ~30s stay no-op fetches.
 */
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { isValidRange } from "../lib/dateWindow";
import { type ApiError, apiFetch } from "./client";
import type { components } from "./schema.gen";
import type { ShowMembers } from "./useCycleRoster";

export type TeamRangeRosterResponse = components["schemas"]["RangeRosterResponse"];
// The member-facing endpoint returns a deliberately narrower payload
// (no `blocks`; `MemberRosterAssignment` rows carry no note / takeover /
// swap_history / publish-state; windows carry no `cycle_id` /
// `unpublished_count`). Every consumer reads those fields optionally,
// so the member shape is render-compatible — the missing inner-circle
// fields simply resolve to empty / undefined (same posture as
// `MemberCycleRosterResponse` in `useCycleRoster`).
export type MemberTeamRangeRosterResponse = components["schemas"]["MemberRangeRosterResponse"];
export type RangeRosterWindow = components["schemas"]["RangeRosterWindow"];
export type RangeRosterDay = components["schemas"]["RangeRosterDay"];
/** #2266 — one pin the lead's Who's on lists (`listedPins: true`). */
export type ListedPin = components["schemas"]["ListedPin"];

const STALE_TIME_MS = 30_000;

/**
 * Build the canonical query key for the range-roster cache. Exported so
 * callers can `queryClient.invalidateQueries({ queryKey: ... })` after a
 * mutation that changes the underlying windows (publish, cell edit)
 * without re-deriving the shape.
 *
 * The member-facing view fetches a different endpoint with a stripped
 * payload, so it is cached under a distinct key suffix to avoid a
 * member's read-only payload masquerading as the lead's full payload in
 * the shared cache (or vice-versa) — same discipline as
 * `cycleRosterQueryKey`.
 */
export function teamRangeRosterQueryKey(
  teamId: string,
  from: string,
  to: string,
  accountId: string | null,
  memberView = false,
  show: ShowMembers | null = null,
  listedPins = false,
  versionId: string | null = null,
): readonly unknown[] {
  // #1956 — the Show filter is the LAST slot (as on `cycleRosterQueryKey`),
  // so a prefix key without it still matches every variant. #2266 — the
  // listed-pins read adds one slot after it, so those prefixes match it too.
  return [
    "team-range-roster",
    memberView ? "member" : "lead",
    teamId,
    from,
    to,
    accountId ?? "",
    show ?? "",
    ...(listedPins ? ["listed-pins"] : []),
    // #3002 — a version read (lead only) adds two slots at the end.
    ...(versionId && !memberView ? ["version", versionId] : []),
  ];
}

interface UseTeamRangeRosterOpts {
  /** #1909 / #1956 — the toolbar's Show control, as on `useCycleRoster`. */
  show?: ShowMembers | null;
  /** #1956 — poll while a range solve the lead just started runs. */
  refetchInterval?: number | false;
  /**
   * Force-disable the fetch even when ids are present. Used by callers
   * that must wait for the viewer's role to resolve before picking the
   * lead vs member endpoint (the lead endpoint 404s for members).
   */
  enabled?: boolean;
  /**
   * Fetch the MEMBER-safe endpoint
   * (`/api/teams/{id}/roster/member?from=&to=`) instead of the lead
   * endpoint. Same continuous days × members grid read-only, with all
   * inner-circle data stripped server-side (#1261 completeness
   * projection applied per window before stitching).
   */
  memberView?: boolean;
  /**
   * #2266 — ask the LEAD read for `listed_pins`: the approved pins the
   * lead's Who's on lists, each with `fills_seat` (the rule Telegram
   * `/whoson` lists with). Two small extra reads server-side, and only when
   * a pin is in range, so only the phone's one-day Who's on asks. Ignored on
   * the member read (members never see pins).
   */
  listedPins?: boolean;
  /**
   * #3002 — draw this saved version read-only on the window that owns it;
   * the other windows on screen keep their live rows. Lead read only (the
   * member read never takes it).
   */
  versionId?: string | null;
}

export function useTeamRangeRoster(
  teamId: string | undefined,
  from: string,
  to: string,
  opts: UseTeamRangeRosterOpts = {},
) {
  // Auth is attached centrally by `apiFetch` via the module-level
  // TokenSource (#773); `useAuth().accountId` is read purely as the
  // per-account cache-key discriminator + the auth-ready enable gate
  // (#853) — same as `useCycleRoster`.
  const { accountId } = useAuth();
  const enabledByCaller = opts.enabled ?? true;
  const memberView = opts.memberView ?? false;
  const show = opts.show ?? null;
  const listedPins = !memberView && (opts.listedPins ?? false);
  const base = memberView
    ? `/api/teams/${teamId}/roster/member?from=${from}&to=${to}`
    : `/api/teams/${teamId}/roster?from=${from}&to=${to}`;
  const withShow = show ? `${base}&show=${show}` : base;
  const withPins = listedPins ? `${withShow}&listed_pins=true` : withShow;
  const versionId = memberView ? null : (opts.versionId ?? null);
  const path = versionId ? `${withPins}&version=${encodeURIComponent(versionId)}` : withPins;
  return useQuery<TeamRangeRosterResponse, ApiError | Error>({
    queryKey: teamRangeRosterQueryKey(
      teamId ?? "",
      from,
      to,
      accountId,
      memberView,
      show,
      listedPins,
      versionId,
    ),
    // An inverted range 422s server-side (cap: 62 days) — don't fire it.
    enabled:
      Boolean(teamId && accountId && from && to && isValidRange(from, to)) && enabledByCaller,
    staleTime: STALE_TIME_MS,
    retry: false,
    refetchInterval: opts.refetchInterval ?? false,
    queryFn: () => apiFetch<TeamRangeRosterResponse>(path),
  });
}
